import { useState } from 'react';
import {
  Package, X, ArrowClockwise,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { LocationPicker } from '@/components/registration';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import { EngAssetFormDialog } from '@/pages/engineering/EngAssetFormDialog';
import { API } from './shared';

// 検索ソースのバッジ配色 — mfg=製造管理 / eng=技術資産 / it=IT資産 / office=備品
const SRC_BADGE = {
  mfg:    'bg-orange-50 text-orange-700 border-orange-200',
  eng:    'bg-teal-50 text-teal-700 border-teal-200',
  it:     'bg-violet-50 text-violet-700 border-violet-200',
  office: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  // B2（仕様 2.5）· 設備台帳の行も受注の品目として選べる
  equip:  'bg-sky-50 text-sky-700 border-sky-200',
};

/* ─── Single-line editor card (search-only, master-picked items) ─── */
export function LineCard({ line, index, canRemove, onChange, onRemove }) {
  const [itemQuery, setItemQuery] = useState('');
  // 1b-ii · 選んだマスターの登録先台帳（表示専用・行には保存しない）
  const [pickedLedger, setPickedLedger] = useState(null);
  // 未登録の品目は、この場で技術資産として登録する（共通ブロック＋品番ルール）
  const [newItemOpen, setNewItemOpen] = useState(false);

  // 横断検索の口は従来どおり（新しいAPIは作らない）。殻だけ共通部品に寄せた。
  const searchMasters = (q) => axios
    .get(`${API}/parts-request/master-search`, { params: { q } })
    .then((r) => r.data || []);

  const pickItem = (it) => {
    onChange({
      item_id: it.id,
      item_source: it.source || 'mfg',
      part_no: it.sku || it.model_no || '',
      // 12-D（2026-11-04）· 自社製作の品目は 手配区分 も自社製作で入る。
      // 購入品に決め打ちすると 製作依頼 のはずが 部品依頼 になってしまう。
      make_type: it.make_type === 'in_house' ? 'in_house' : 'purchase',
      name: it.name || '',
      drawing_no: it.drawing_no || '',
      maker: it.maker || '',
      maker_pn: it.maker_pn || '',
      model_no: it.model_no || '',
      material: it.material || '',
      uom: it.uom || '個',
      reference_url: it.reference_url || '',
    });
    // 1b-ii · 登録先台帳は**行に保存しない**（派生値をレコードに複製しない）。
    // 選択したマスターの値をこのカードの表示用ローカル状態にだけ持つ。
    setPickedLedger(it.ledger_label
      ? { label: it.ledger_label, screen: it.ledger_screen || '', foreign: !!it.ledger_foreign }
      : null);
    setItemQuery('');
  };

  const clearItem = () => {
    onChange({
      item_id: '', item_source: '', name: '', part_no: '', drawing_no: '', maker: '',
      maker_pn: '', model_no: '', material: '', reference_url: '',
    });
    setPickedLedger(null);
  };

  const hasPicked = !!line.item_id;

  // Build the compact bullet list of spec fields for a picked item
  const specBullets = [
    { label: '品番',       value: line.part_no },
    { label: '図番',       value: line.drawing_no },
    { label: '型式',       value: line.model_no },
    { label: 'メーカー',    value: line.maker },
    { label: 'メーカー品番', value: line.maker_pn },
    { label: '材質',       value: line.material },
  ].filter((s) => (s.value || '').toString().trim());

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm p-4 pl-6 relative" data-testid={`pr-line-${index}`}>
      {/* Line index badge */}
      <div className="absolute -left-2.5 top-4 w-6 h-6 rounded-full bg-teal-700 text-white text-xs font-bold flex items-center justify-center shadow ring-2 ring-white">
        {index + 1}
      </div>

      {canRemove && (
        <button onClick={onRemove} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-zinc-100 hover:bg-rose-100 hover:text-rose-600 text-zinc-500 flex items-center justify-center transition-colors" data-testid={`pr-line-remove-${index}`}>
          <X size={14} weight="bold" />
        </button>
      )}

      {!hasPicked ? (
        /* ─── 品目はひとつの共通ピッカーから選ぶ（標準ルール §7.1） ─── */
        <div className="space-y-2">
          <MasterItemPicker
            testIdPrefix={`pr-line-${index}`}
            label="品目（登録マスターから検索）＊"
            placeholder="品名・品番・図番・メーカー品番で全マスターを検索..."
            search={searchMasters}
            sources={['mfg', 'eng', 'it', 'office', 'equip']}
            onPick={pickItem}
            onQueryChange={setItemQuery}
            emptyText="該当する登録マスターがありません。品番の無い品目は依頼できません。"
            emptyAction={(
              <Button type="button" variant="outline" className="h-9 w-full text-[12px]"
                onClick={() => setNewItemOpen(true)}
                data-testid={`pr-line-new-item-${index}`}>
                新規品目として登録（品番は品番ルールから発番）
              </Button>
            )}
            renderResultExtra={(it) => (
              <span className="flex flex-col items-end gap-0.5">
                <span className={`rounded border px-1.5 text-[10px] font-semibold ${SRC_BADGE[it.source] || SRC_BADGE.mfg}`}>
                  {it.source_label || '製造'}
                </span>
                {it.on_hand !== undefined && it.on_hand !== null && (
                  <span className={`rounded-full border px-2 text-[10px] font-semibold ${it.on_hand > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'}`}>
                    在庫 {it.on_hand}{it.uom || '個'}
                  </span>
                )}
              </span>
            )}
            helper="製造管理・技術資産・IT資産・備品・設備の登録マスターを横断検索できます。未登録の品目は「新規品目として登録」から登録してください（品名・品番の手入力はできません）。"
          />
        </div>
      ) : (
        /* ─── Picked state: read-only summary card + qty input ─── */
        <div className="space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Package size={14} weight="fill" className="text-emerald-600 shrink-0" />
                  <span className="text-sm font-semibold text-zinc-900 truncate" data-testid={`pr-line-name-${index}`}>
                    {line.name || '（品名なし）'}
                  </span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-100 border border-emerald-200 text-[10px] font-semibold text-emerald-700">
                    マスター登録済
                  </span>
                  {/* 1b-ii · この行の品目がどの台帳の持ち物かを読み取り専用で出す。
                      台帳名・画面名は master-search が返す値のみ（画面に文字列を持たない）。
                      ここに品番グループの選択肢は置かない。 */}
                  {pickedLedger && (
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${
                        pickedLedger.foreign
                          ? 'bg-purple-50 text-purple-700 border-purple-200'
                          : 'bg-white text-zinc-600 border-zinc-200'}`}
                      title={pickedLedger.screen}
                      data-testid={`pr-line-ledger-${index}`}
                    >
                      {pickedLedger.label}
                    </span>
                  )}
                </div>
                {specBullets.length > 0 && (
                  <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-testid={`pr-line-specs-${index}`}>
                    {specBullets.map((s) => (
                      <li key={s.label} className="flex items-baseline gap-1.5 text-[12px]">
                        <span className="w-2 h-2 rounded-full bg-emerald-400/70 shrink-0 translate-y-[3px]" />
                        <span className="text-zinc-500 shrink-0">{s.label}:</span>
                        <span className="font-mono text-zinc-800 truncate">{s.value}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={clearItem}
                className="h-8 shrink-0 text-xs"
                data-testid={`pr-line-change-${index}`}
                title="別の品目を選び直す"
              >
                <ArrowClockwise size={12} weight="bold" className="mr-1" />
                変更
              </Button>
            </div>
          </div>

          {/* Quantity only */}
          <div className="grid grid-cols-4 gap-3 items-end">
            <div className="col-span-2 sm:col-span-1">
              <Label className="text-xs font-semibold text-zinc-600">
                数量<span className="text-rose-500">＊</span>
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={line.qty}
                  onChange={(e) => onChange({ qty: e.target.value })}
                  className="h-11 font-semibold text-lg tabular-nums text-center w-28"
                  data-testid={`pr-line-qty-${index}`}
                />
                <span className="text-sm font-semibold text-zinc-600" data-testid={`pr-line-uom-${index}`}>
                  {line.uom || '個'}
                </span>
              </div>
            </div>
          </div>

          {/* 手配区分（2026-06） — 購入品は REQ → TECHS のまま。
              自社製作は TECHS に出さず、技術の 製作依頼 になる。 */}
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-2.5 space-y-2"
            data-testid={`pr-line-maketype-box-${index}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-xs font-semibold text-zinc-600">手配区分</Label>
              {[{ v: 'purchase', l: '購入品' }, { v: 'in_house', l: '自社製作' }].map((o) => (
                <button key={o.v} type="button"
                  onClick={() => onChange({ make_type: o.v })}
                  data-testid={`pr-line-maketype-${o.v}-${index}`}
                  className={`rounded-full px-3 py-1 text-[12px] font-semibold border transition-colors ${
                    (line.make_type || 'purchase') === o.v
                      ? 'bg-teal-700 text-white border-teal-700'
                      : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
                  {o.l}
                </button>
              ))}
              <span className="text-[11px] text-zinc-500">
                {(line.make_type || 'purchase') === 'in_house'
                  ? '技術の製作依頼（FAB）になります。TECHSには出ません。'
                  : '部品依頼（REQ）→ TECHS へ進みます。'}
              </span>
            </div>
            {/* 手配区分が購入品でも、単品の物（設備 / IT資産）は入荷のときに
                1台=1レコードで作る（§8.7）。その置き場所をここで決める。 */}
            {(line.make_type || 'purchase') !== 'in_house'
              && ['equip', 'it'].includes(line.item_source) && (
              <div>
                <Label className="text-xs font-semibold text-zinc-600">
                  納入先（場所）<span className="ml-1 font-normal text-zinc-400">
                    入荷すると1台ずつレコードを作り、この場所に置きます
                  </span>
                </Label>
                <div className="mt-1">
                  <LocationPicker value={line.deliver_location_id || ''}
                    legacyText={line.deliver_location_name || ''}
                    onChange={(id, row) => onChange({
                      deliver_location_id: id || '',
                      deliver_location_name: (row && (row.path || row.name)) || '',
                    })}
                    testId={`pr-line-unit-location-${index}`} />
                </div>
              </div>
            )}
            {(line.make_type === 'in_house') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">
                    納入先（部署）<span className="text-rose-500">＊</span>
                  </Label>
                  <select value={line.deliver_register || ''}
                    onChange={(e) => onChange({ deliver_register: e.target.value })}
                    className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                    data-testid={`pr-line-deliver-register-${index}`}>
                    <option value="">選択してください</option>
                    <option value="eng">技術</option>
                    <option value="mfg">製造</option>
                    <option value="it">IT</option>
                    <option value="bihin">備品</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">納入先（場所）</Label>
                  <div className="mt-1">
                    <LocationPicker value={line.deliver_location_id || ''}
                      legacyText={line.deliver_location_name || ''}
                      onChange={(id, row) => onChange({
                        deliver_location_id: id || '',
                        deliver_location_name: (row && (row.path || row.name)) || '',
                      })}
                      testId={`pr-line-deliver-location-${index}`} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Per-line notes */}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">備考（この品目について）</Label>
            <Input
              value={line.notes || ''}
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="例: 前回と同じメーカーでお願いします"
              className="h-10 mt-1"
              data-testid={`pr-line-notes-${index}`}
            />
          </div>
        </div>
      )}

      {/* 未登録の品目をこの場で登録する（F1 と同じフォーム・同じ品番ルール）。
          保存するとこの行がその品目を選んだ状態になる。 */}
      <EngAssetFormDialog
        open={newItemOpen}
        onClose={() => setNewItemOpen(false)}
        prefill={{ name: itemQuery }}
        onSaved={(asset) => {
          setNewItemOpen(false);
          if (asset?.id) {
            pickItem({ id: asset.id, source: 'eng', name: asset.name, sku: asset.sku,
                       uom: asset.uom, maker: asset.maker, maker_pn: asset.maker_pn,
                       model_no: asset.model_no, drawing_no: asset.drawing_no,
                       material: asset.material, reference_url: asset.reference_url });
          }
        }}
      />
    </div>
  );
}
